/**
 * Constants and configuration values for the Identification Form
 */

// Validation Constants
/**
 * @deprecated As of 2026-01-10, businessDescription field is now optional with no minimum length requirement.
 * This constant is kept for backwards compatibility but is no longer used in validation.
 */
export const MIN_BUSINESS_DESC_LENGTH = 50;
export const MIN_EXECUTIVES = 1;
export const MIN_BIRTH_YEAR = '1900-01-01';

// Legal Form Categories
export const CORPORATE_FORMS = [
  'ag',
  'gmbh',
  'kommanditaktiengesellschaft',
  'other',
] as const;

/**
 * Get legal form options with translations
 * @param tForms Translation function
 * @returns Array of legal form options
 */
export const getLegalForms = (tForms: (key: string) => string) => [
  {
    value: 'einzelunternehmen',
    label: tForms('identification.legalForms.einzelunternehmen'),
  },
  {
    value: 'einfache_gesellschaft',
    label: tForms('identification.legalForms.einfache_gesellschaft'),
  },
  {
    value: 'kollektivgesellschaft',
    label: tForms('identification.legalForms.kollektivgesellschaft'),
  },
  { value: 'ag', label: tForms('identification.legalForms.ag') },
  { value: 'gmbh', label: tForms('identification.legalForms.gmbh') },
  {
    value: 'oeffentlich_rechtliche_institution',
    label: tForms(
      'identification.legalForms.oeffentlich_rechtliche_institution'
    ),
  },
  {
    value: 'kommanditaktiengesellschaft',
    label: tForms('identification.legalForms.kommanditaktiengesellschaft'),
  },
  {
    value: 'kommanditgesellschaft',
    label: tForms('identification.legalForms.kommanditgesellschaft'),
  },
  { value: 'verein', label: tForms('identification.legalForms.verein') },
  {
    value: 'stiftung',
    label: tForms('identification.legalForms.stiftung'),
  },
  {
    value: 'genossenschaft',
    label: tForms('identification.legalForms.genossenschaft'),
  },
  { value: 'other', label: tForms('identification.legalForms.other') },
];

/**
 * Utility function to check if a legal form is a corporate form
 * Centralizes the logic for determining corporate forms to avoid duplication
 * @param legalForm - Legal form value to check
 * @returns True if the legal form is a corporate form, false otherwise
 */
export const isCorporateForm = (legalForm: string | undefined): boolean => {
  if (!legalForm) return false;
  const normalizedForm = legalForm.toLowerCase();
  return CORPORATE_FORMS.includes(
    normalizedForm as (typeof CORPORATE_FORMS)[number]
  );
};

/**
 * Utility function to check if a legal form should display the listed company field
 * Listed company field is shown for all corporate forms except GmbH
 * @param legalForm - Legal form value to check
 * @returns True if the listed company field should be shown
 */
export const shouldShowListedCompanyField = (
  legalForm: string | undefined
): boolean => {
  if (!legalForm) return false;
  const normalizedForm = legalForm.toLowerCase();
  return isCorporateForm(legalForm) && normalizedForm !== 'gmbh';
};

/**
 * Get financial intermediary type options with translations
 * @param tForms Translation function
 * @returns Array of financial intermediary type options
 */
export const getFinancialIntermediaryTypes = (
  tForms: (key: string) => string
) => [
  {
    value: 'bank',
    label: tForms('identification.financialIntermediaryTypes.bank'),
  },
  {
    value: 'vorsorge_einrichtung',
    label: tForms(
      'identification.financialIntermediaryTypes.vorsorge_einrichtung'
    ),
  },
  {
    value: 'versicherung',
    label: tForms('identification.financialIntermediaryTypes.versicherung'),
  },
  {
    value: 'spielbank',
    label: tForms('identification.financialIntermediaryTypes.spielbank'),
  },
  {
    value: 'wechselstube',
    label: tForms('identification.financialIntermediaryTypes.wechselstube'),
  },
  {
    value: 'money_transmitter',
    label: tForms(
      'identification.financialIntermediaryTypes.money_transmitter'
    ),
  },
  {
    value: 'other',
    label: tForms('identification.financialIntermediaryTypes.other'),
  },
];
